import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RetryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  SkipNext as SkipIcon,
  AddCircle as CreatedIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import FlexService from '../../../services/FlexService';

const STATUS_CONFIG = {
  created: { icon: CreatedIcon, color: '#4caf50', label: 'Created' },
  updated: { icon: SuccessIcon, color: '#2196f3', label: 'Updated' },
  skipped: { icon: SkipIcon, color: '#9e9e9e', label: 'Skipped' },
  error:   { icon: ErrorIcon, color: '#f44336', label: 'Error' },
};

const SyncFromFilesPanel = ({ colors, onClose, onSyncComplete }) => {
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryingPath, setRetryingPath] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [validate, setValidate] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  // Editor dialog state
  const [editFile, setEditFile] = useState(null); // { path, filename }
  const [editContent, setEditContent] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const originalContentRef = useRef('');

  const hasErrors = files.some(f => f.status === 'error');
  const parseWarningCount = files.filter(f => f.parseErrors).length;

  const filteredFiles = useMemo(() => {
    if (statusFilter === 'parse_errors') return files.filter(f => f.parseErrors);
    if (statusFilter !== 'all') return files.filter(f => f.status === statusFilter);
    return files;
  }, [files, statusFilter]);

  const recalcSummary = (fileList) => {
    setSummary(s => ({
      ...s,
      created: fileList.filter(f => f.status === 'created').length,
      updated: fileList.filter(f => f.status === 'updated').length,
      skipped: fileList.filter(f => f.status === 'skipped').length,
      errors: fileList.filter(f => f.status === 'error').length,
    }));
  };

  const handleSync = async () => {
    setLoading(true);
    setStatusFilter('all');
    try {
      const result = await FlexService.syncFromFiles(validate);
      // Response: { scripts: {..., files: [...]}, agents: {..., files: [...]}, workspaces: {..., files: [...]} }
      // or flat (legacy): { totalFiles, created, files, ... }
      const scriptResult = result.scripts || result;
      const agentResult = result.agents || {};
      const wsResult = result.workspaces || {};

      // Merge all file results into one list, tagging each with artifactType
      const scriptFiles = (scriptResult.files || []).map(f => ({ ...f, artifactType: f.artifactType || 'script' }));
      const agentFiles = (agentResult.files || []).map(f => ({ ...f, artifactType: 'agent' }));
      const wsFiles = (wsResult.files || []).map(f => ({ ...f, artifactType: 'workspace' }));
      setFiles([...scriptFiles, ...agentFiles, ...wsFiles]);

      const totalCreated = (scriptResult.created || 0) + (agentResult.created || 0) + (wsResult.created || 0);
      const totalUpdated = (scriptResult.updated || 0) + (agentResult.updated || 0) + (wsResult.updated || 0);
      const totalSkipped = (scriptResult.skipped || 0) + (agentResult.skipped || 0) + (wsResult.skipped || 0);
      const totalErrors = (scriptResult.errors?.length || 0) + (agentResult.errors?.length || 0) + (wsResult.errors?.length || 0);

      setSummary({
        total: (scriptResult.totalFiles || 0) + (agentResult.totalFiles || 0) + (wsResult.totalFiles || 0),
        created: totalCreated,
        updated: totalUpdated,
        skipped: totalSkipped,
        errors: totalErrors,
      });

      try {
        await FlexService.evictAllCaches();
      } catch (cacheErr) {
        console.warn('Sync succeeded but cache clear failed:', cacheErr);
      }

      if (onSyncComplete) onSyncComplete();
    } catch (err) {
      setSummary({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (file) => {
    setRetryingPath(file.path);
    try {
      const result = await FlexService.retrySyncFile(file.path);
      const updated = files.map(f => f.path === file.path ? { ...f, ...result } : f);
      setFiles(updated);
      recalcSummary(updated);
      if (onSyncComplete) onSyncComplete();
    } catch (err) {
      setFiles(prev => prev.map(f => f.path === file.path ? { ...f, status: 'error', reason: err.message } : f));
    } finally {
      setRetryingPath(null);
    }
  };

  const handleOpenEditor = useCallback(async (file) => {
    setEditFile(file);
    setEditLoading(true);
    setEditDirty(false);
    try {
      const result = await FlexService.readFileContent(file.path);
      setEditContent(result.content || '');
      originalContentRef.current = result.content || '';
    } catch (err) {
      setEditContent(`// Error loading file: ${err.message}`);
      originalContentRef.current = '';
    } finally {
      setEditLoading(false);
    }
  }, []);

  const handleEditorChange = useCallback((value) => {
    setEditContent(value || '');
    setEditDirty(value !== originalContentRef.current);
  }, []);

  const handleSaveEditor = useCallback(async () => {
    if (!editFile) return;
    setEditSaving(true);
    try {
      const result = await FlexService.writeFileContent(editFile.path, editContent);
      // Update the file row with the new sync result
      const updated = files.map(f => f.path === editFile.path ? { ...f, ...result } : f);
      setFiles(updated);
      recalcSummary(updated);
      originalContentRef.current = editContent;
      setEditDirty(false);
      if (onSyncComplete) onSyncComplete();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setEditSaving(false);
    }
  }, [editFile, editContent, files, onSyncComplete]);

  const handleCloseEditor = useCallback(() => {
    if (editDirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setEditFile(null);
    setEditContent('');
    setEditDirty(false);
  }, [editDirty]);

  const handleClose = () => {
    if (hasErrors) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  };

  const thStyle = { bgcolor: colors.surface, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, py: 0.5, fontSize: '0.75rem' };

  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      bgcolor: colors.background, zIndex: 10,
      display: 'flex', flexDirection: 'column',
      border: `1px solid ${colors.border}`,
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, borderBottom: `1px solid ${colors.border}`,
        bgcolor: colors.headerBg || colors.surface,
      }}>
        <Typography variant="subtitle1" sx={{ color: colors.text, fontWeight: 600 }}>
          Sync From Files
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={validate}
                onChange={(e) => setValidate(e.target.checked)}
                disabled={loading}
                sx={{ color: colors.textSecondary, '&.Mui-checked': { color: colors.accent }, p: 0.25 }}
              />
            }
            label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Validate syntax</Typography>}
            sx={{ mr: 1 }}
          />
          {!loading && files.length === 0 && (
            <Button variant="contained" size="small" onClick={handleSync}
              sx={{ bgcolor: colors.success, borderRadius: 1, textTransform: 'none', '&:hover': { bgcolor: colors.successDark || colors.success } }}>
              Start Sync
            </Button>
          )}
          {!loading && files.length > 0 && (
            <Button variant="outlined" size="small" onClick={handleSync}
              sx={{ borderColor: colors.border, color: colors.text, borderRadius: 1, textTransform: 'none' }}>
              Re-sync All
            </Button>
          )}
          {loading && <CircularProgress size={20} sx={{ color: colors.accent }} />}
          <Tooltip title="Close">
            <IconButton size="small" onClick={handleClose} sx={{ color: colors.textSecondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary bar — clickable filter chips */}
      {summary && !summary.error && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
          borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
          flexWrap: 'wrap',
        }}>
          <Chip label={`${summary.total} files`} size="small"
            onClick={() => setStatusFilter('all')}
            sx={{ cursor: 'pointer', bgcolor: statusFilter === 'all' ? colors.border : 'transparent', color: colors.text, fontWeight: statusFilter === 'all' ? 600 : 400 }} />
          {[
            { key: 'created', count: summary.created, color: '#4caf50' },
            { key: 'updated', count: summary.updated, color: '#2196f3' },
            { key: 'skipped', count: summary.skipped, color: '#9e9e9e' },
          ].map(({ key, count, color }) => (
            <Chip key={key} label={`${count} ${key}`} size="small"
              onClick={() => setStatusFilter(f => f === key ? 'all' : key)}
              sx={{ cursor: 'pointer', bgcolor: statusFilter === key ? `${color}33` : `${color}22`, color, fontWeight: statusFilter === key ? 600 : 400 }} />
          ))}
          {summary.errors > 0 && (
            <Chip label={`${summary.errors} errors`} size="small"
              onClick={() => setStatusFilter(f => f === 'error' ? 'all' : 'error')}
              sx={{ cursor: 'pointer', bgcolor: statusFilter === 'error' ? 'rgba(244,67,54,0.3)' : 'rgba(244,67,54,0.15)', color: '#f44336', fontWeight: statusFilter === 'error' ? 600 : 400 }} />
          )}
          {parseWarningCount > 0 && (
            <Chip label={`${parseWarningCount} parse errors`} size="small"
              icon={<WarningIcon sx={{ fontSize: 14, color: '#ff9800 !important' }} />}
              onClick={() => setStatusFilter(f => f === 'parse_errors' ? 'all' : 'parse_errors')}
              sx={{ cursor: 'pointer', bgcolor: statusFilter === 'parse_errors' ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.15)', color: '#ff9800', fontWeight: statusFilter === 'parse_errors' ? 600 : 400 }} />
          )}
          {/* Agent and Workspace sync counts */}
          {(summary.agentsTotal > 0 || summary.workspacesTotal > 0) && (
            <Box sx={{ ml: 1, pl: 1, borderLeft: `1px solid ${colors.border}`, display: 'flex', gap: 0.5 }}>
              {summary.agentsTotal > 0 && (
                <Chip label={`${summary.agentsCreated + summary.agentsUpdated}/${summary.agentsTotal} agents`} size="small"
                  sx={{ bgcolor: 'rgba(167,139,250,0.15)', color: '#a78bfa' }} />
              )}
              {summary.workspacesTotal > 0 && (
                <Chip label={`${summary.workspacesCreated + summary.workspacesUpdated}/${summary.workspacesTotal} workspaces`} size="small"
                  sx={{ bgcolor: 'rgba(34,211,238,0.15)', color: '#22d3ee' }} />
              )}
            </Box>
          )}
        </Box>
      )}

      {summary?.error && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(244,67,54,0.1)' }}>
          <Typography variant="body2" sx={{ color: '#f44336' }}>Sync failed: {summary.error}</Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loading && files.length === 0 && !summary?.error && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Import .flex scripts, .agent definitions, and .workspace files from the file system into the database.
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            Check "Validate syntax" to also parse each script for errors.
          </Typography>
        </Box>
      )}

      {/* Results table */}
      {files.length > 0 && (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, width: 70 }}>Type</TableCell>
                <TableCell sx={thStyle}>Domain</TableCell>
                <TableCell sx={thStyle}>Category</TableCell>
                <TableCell sx={thStyle}>Filename</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Reason</TableCell>
                {validate && <TableCell sx={thStyle}>Parse</TableCell>}
                <TableCell sx={{ ...thStyle, width: 70 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFiles.map((file) => {
                const cfg = STATUS_CONFIG[file.status] || STATUS_CONFIG.error;
                const StatusIcon = cfg.icon;
                const parseLines = file.parseErrors ? file.parseErrors.split('\n') : [];
                return (
                  <TableRow key={file.path || file.name} sx={{ '&:hover': { bgcolor: colors.hover || 'rgba(255,255,255,0.03)' } }}>
                    <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.5 }}>
                      <Typography variant="caption" sx={{
                        fontSize: '0.7rem', fontWeight: 600, px: 0.5, py: 0.15, borderRadius: 0.5,
                        bgcolor: file.artifactType === 'agent' ? 'rgba(167,139,250,0.15)' : file.artifactType === 'workspace' ? 'rgba(34,211,238,0.15)' : 'rgba(34,197,94,0.15)',
                        color: file.artifactType === 'agent' ? '#a78bfa' : file.artifactType === 'workspace' ? '#22d3ee' : '#22c55e',
                      }}>
                        {file.artifactType === 'agent' ? 'Agent' : file.artifactType === 'workspace' ? 'Workspace' : 'Script'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: colors.text, borderBottom: `1px solid ${colors.border}`, py: 0.5 }}>{file.domain || '-'}</TableCell>
                    <TableCell sx={{ color: colors.text, borderBottom: `1px solid ${colors.border}`, py: 0.5 }}>{file.category || '-'}</TableCell>
                    <TableCell sx={{ color: colors.text, borderBottom: `1px solid ${colors.border}`, py: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>{file.filename || file.name || '-'}</TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StatusIcon sx={{ fontSize: 16, color: cfg.color }} />
                        <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 500 }}>{cfg.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, py: 0.5, fontSize: '0.8rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={file.reason || ''} placement="top-start">
                        <span>{file.reason || ''}</span>
                      </Tooltip>
                    </TableCell>
                    {validate && (
                      <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.5, maxWidth: 250 }}>
                        {parseLines.length > 0 ? (
                          <Tooltip title={
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {parseLines.map((line, i) => <div key={i}>{line}</div>)}
                            </Box>
                          } placement="top-start">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WarningIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                              <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.75rem' }}>
                                {parseLines.length} error{parseLines.length > 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : file.status !== 'error' && file.status !== 'skipped' ? (
                          <Typography variant="caption" sx={{ color: '#4caf50' }}>OK</Typography>
                        ) : null}
                      </TableCell>
                    )}
                    <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Edit script">
                          <IconButton size="small" onClick={() => handleOpenEditor(file)} sx={{ color: colors.textSecondary, p: 0.25, '&:hover': { color: colors.accent } }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        {file.status === 'error' && (
                          <Tooltip title="Retry sync">
                            <IconButton size="small" onClick={() => handleRetry(file)}
                              disabled={retryingPath === file.path} sx={{ color: colors.accent, p: 0.25 }}>
                              {retryingPath === file.path
                                ? <CircularProgress size={14} sx={{ color: colors.accent }} />
                                : <RetryIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Editor Dialog */}
      <Dialog
        open={!!editFile}
        onClose={handleCloseEditor}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.background, color: colors.text, height: '80vh' } }}
      >
        <DialogTitle sx={{
          color: colors.text, py: 1, px: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {editFile?.filename}
            </Typography>
            {editDirty && (
              <Chip label="modified" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800' }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>{editFile?.path}</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {editLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: colors.accent }} />
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Editor
                height="100%"
                language="flex"
                theme="vs-dark"
                value={editContent}
                onChange={handleEditorChange}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 2, py: 1 }}>
          <Button onClick={handleCloseEditor} disabled={editSaving} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditor}
            disabled={editSaving || !editDirty}
            startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            variant="contained"
            sx={{ bgcolor: colors.success, textTransform: 'none', '&:hover': { bgcolor: colors.successDark || colors.success }, '&.Mui-disabled': { bgcolor: colors.border } }}
          >
            {editSaving ? 'Saving...' : 'Save & Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm close with errors dialog */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} PaperProps={{ sx: { bgcolor: colors.surface, color: colors.text } }}>
        <DialogTitle sx={{ color: colors.text }}>Close with errors?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            There are {files.filter(f => f.status === 'error').length} file(s) with errors. Are you sure you want to close?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} sx={{ color: colors.textSecondary }}>Cancel</Button>
          <Button onClick={onClose} sx={{ color: '#f44336' }}>Close Anyway</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyncFromFilesPanel;
